/**
 * Modal Component Suite
 * Complete modal implementation with focus trap, scroll lock, and accessibility
 */

import React, { forwardRef, createContext, useContext, useEffect, useRef, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box } from '../primitives/Box';
import { Flex } from '../primitives/Flex';
import { useMultiStyleConfig } from '../hooks/useStyleConfig';

/**
 * Modal Context
 */
const ModalContext = createContext({});

export const useModalContext = () => useContext(ModalContext);

// Shared animation duration for subtle transitions
const ANIMATION_DURATION = 200;

/**
 * Modal Component
 * Main wrapper providing context and portal
 * 
 * @example
 * <Modal isOpen={isOpen} onClose={onClose}>
 *   <ModalOverlay />
 *   <ModalContent>
 *     <ModalHeader>Title</ModalHeader>
 *     <ModalBody>Content</ModalBody>
 *     <ModalFooter>Actions</ModalFooter>
 *   </ModalContent>
 * </Modal>
 */
export const Modal = ({
  isOpen,
  onClose,
  children,
  size = 'md',
  isCentered = false,
  scrollBehavior = 'outside',
  closeOnOverlayClick = true,
  closeOnEsc = true,
  blockScrollOnMount = true,
  preserveScrollBarGap = true,
  returnFocusOnClose = true,
  autoFocus = true,
  trapFocus = true,
  initialFocusRef,
  finalFocusRef,
  id,
  motionPreset = 'scale',
  ...props
}) => {
  const contentRef = useRef(null);
  const previousActiveElement = useRef(null);
  const [render, setRender] = useState(isOpen);
  const [visible, setVisible] = useState(isOpen);

  // Store the previously focused element
  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement;
    }
  }, [isOpen]);

  // Handle escape key
  // useEffect(() => {
  //   if (!isOpen || !closeOnEsc) return;

  //   const handleKeyDown = (e) => {
  //     if (e.key === 'Escape') {
  //       onClose();
  //     }
  //   };

  //   document.addEventListener('keydown', handleKeyDown);
  //   return () => document.removeEventListener('keydown', handleKeyDown);
  // }, [isOpen, closeOnEsc, onClose]);

  // Handle scroll lock
  useEffect(() => {
    if (!blockScrollOnMount) return;

    if (isOpen) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';

      if (preserveScrollBarGap) {
        const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.paddingRight = `${scrollBarWidth}px`;
      }

      return () => {
        document.body.style.overflow = originalStyle;
        document.body.style.paddingRight = '';
      };
    }
  }, [isOpen, blockScrollOnMount, preserveScrollBarGap]);

  // Handle focus on open
  useEffect(() => {
    if (isOpen && autoFocus) {
      if (initialFocusRef?.current) {
        setTimeout(() => initialFocusRef.current?.focus(), 0);
      } else if (contentRef.current) {
        setTimeout(() => {
          const firstFocusable = contentRef.current?.querySelector(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          );
          firstFocusable?.focus();
        }, 0);
      }
    }
  }, [isOpen, autoFocus, initialFocusRef]);

  // Handle focus return on close
  useEffect(() => {
    if (!isOpen && returnFocusOnClose) {
      const elementToFocus = finalFocusRef?.current || previousActiveElement.current;
      elementToFocus?.focus?.();
    }
  }, [isOpen, returnFocusOnClose, finalFocusRef]);

  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget && closeOnOverlayClick) {
      onClose();
    }
  }, [closeOnOverlayClick, onClose]);

  const context = {
    isOpen,
    isVisible: visible,
    onClose,
    size,
    isCentered,
    scrollBehavior,
    contentRef,
    handleOverlayClick,
  };

  // Keep portal mounted while fading out
  // Manage mount/unmount and animated visibility for enter/exit transitions
  useEffect(() => {
    let timeoutId;

    if (isOpen) {
      // mount immediately, then set visible after a short delay
      setRender(true);
      timeoutId = setTimeout(() => setVisible(true), 30);
    } else {
      // start exit animation
      setVisible(false);
      // unmount after animation finishes
      timeoutId = setTimeout(() => setRender(false), ANIMATION_DURATION);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isOpen]);

  if (!render) return null;

  return createPortal(
    <ModalContext.Provider value={context}>
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        zIndex="modal"
        {...props}
      >
        {children}
      </Box>
    </ModalContext.Provider>,
    document.body
  );
};

Modal.displayName = 'Modal';

/**
 * ModalOverlay Component
 * Semi-transparent backdrop
 */
export const ModalOverlay = forwardRef((props, ref) => {
  const { handleOverlayClick, isVisible } = useModalContext();

  return (
    <Box
      ref={ref}
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      bg="blackAlpha.600"
      zIndex="overlay"
      onClick={handleOverlayClick}
      style={{
        transition: `opacity ${ANIMATION_DURATION}ms ease`,
        opacity: isVisible ? 1 : 0,
        pointerEvents: isVisible ? 'auto' : 'none',
      }}
      {...props}
    />
  );
});

ModalOverlay.displayName = 'ModalOverlay';

/**
 * ModalContent Component
 * Main content container with focus trap
 */
export const ModalContent = forwardRef(({
  children,
  ...props
}, ref) => {
  const { size, isCentered, scrollBehavior, contentRef, isVisible } = useModalContext();

  const sizeMap = {
    xs: '20rem',
    sm: '24rem',
    md: '28rem',
    lg: '32rem',
    xl: '36rem',
    '2xl': '42rem',
    '3xl': '48rem',
    '4xl': '56rem',
    '5xl': '64rem',
    '6xl': '72rem',
    full: '100vw',
  };

  const containerStyles = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: isCentered ? 'center' : 'flex-start',
    overflow: scrollBehavior === 'inside' ? 'hidden' : 'auto',
    zIndex: 'modal',
    paddingTop: isCentered ? 0 : '3.75rem',
    paddingBottom: isCentered ? 0 : '3.75rem',
  };

  const contentStyles = {
    bg: 'white',
    borderRadius: 'lg',
    boxShadow: 'lg',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    width: '100%',
    maxWidth: sizeMap[size] || sizeMap.md,
    maxHeight: scrollBehavior === 'inside' ? 'calc(100% - 7.5rem)' : undefined,
    margin: isCentered ? 'auto' : undefined,
    my: isCentered ? 'auto' : '16',
    outline: 0,
  };

  return (
    <Box {...containerStyles}>
      <Box
        ref={(node) => {
          contentRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        {...contentStyles}
        style={{
          transition: `opacity ${ANIMATION_DURATION}ms ease, transform ${ANIMATION_DURATION}ms ease`,
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(-10px) scale(0.98)',
        }}
        {...props}
      >
        {children}
      </Box>
    </Box>
  );
});

ModalContent.displayName = 'ModalContent';

/**
 * ModalHeader Component
 */
export const ModalHeader = forwardRef(({
  children,
  ...props
}, ref) => {
  return (
    <Box
      ref={ref}
      px={6}
      py={4}
      fontSize="xl"
      fontWeight="semibold"
      {...props}
    >
      {children}
    </Box>
  );
});

ModalHeader.displayName = 'ModalHeader';

/**
 * ModalBody Component
 */
export const ModalBody = forwardRef(({
  children,
  ...props
}, ref) => {
  const { scrollBehavior } = useModalContext();

  return (
    <Box
      ref={ref}
      px={6}
      py={2}
      flex={1}
      overflow={scrollBehavior === 'inside' ? 'auto' : undefined}
      {...props}
    >
      {children}
    </Box>
  );
});

ModalBody.displayName = 'ModalBody';

/**
 * ModalFooter Component
 */
export const ModalFooter = forwardRef(({
  children,
  ...props
}, ref) => {
  return (
    <Flex
      ref={ref}
      px={6}
      py={4}
      justify="flex-end"
      gap={3}
      {...props}
    >
      {children}
    </Flex>
  );
});

ModalFooter.displayName = 'ModalFooter';

/**
 * ModalCloseButton Component
 */
export const ModalCloseButton = forwardRef((props, ref) => {
  const { onClose } = useModalContext();

  return (
    <Box
      ref={ref}
      as="button"
      type="button"
      position="absolute"
      top={2}
      right={3}
      display="flex"
      alignItems="center"
      justifyContent="center"
      w={8}
      h={8}
      borderRadius="md"
      bg="transparent"
      cursor="pointer"
      transition="background 0.2s"
      _hover={{ bg: 'gray.100' }}
      onClick={onClose}
      aria-label="Close modal"
      {...props}
    >
      <svg
        viewBox="0 0 24 24"
        width="1em"
        height="1em"
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
      >
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    </Box>
  );
});

ModalCloseButton.displayName = 'ModalCloseButton';

export default Modal;
